import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { CurrentUserService } from '../../common/current-user/current-user.service';
import { ChoosePlanService } from '../../pages/choose-plan/service/choose-plan/choose-plan.service';

/**
 * Subscription-required interstitial for accounts with no subscription at all.
 *
 * Rendered at `/subscription-required` when {@link CurrentUserService.checkFocussedSubscription}
 * finds a Stripe customer with zero subscriptions (typically a legacy account created before
 * subscriptions were provisioned automatically at signup). Single job: explain that a
 * subscription is needed to keep using the platform and drive the "Choose a plan" action
 * (existing ChoosePlan modal → Stripe Checkout) to restore access.
 */
@Component({
  selector: 'app-subscription-required',
  imports: [],
  templateUrl: './subscription-required.html',
  styleUrl: './subscription-required.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SubscriptionRequired {
  private readonly currentUserService = inject(CurrentUserService);
  private readonly choosePlanService = inject(ChoosePlanService);
  private readonly router = inject(Router);

  /** Support contact for the billing-help footer link. */
  readonly supportEmail = 'hello@shoutugc.com';

  private readonly shoutUser = toSignal(this.currentUserService.shoutUser$, { initialValue: null });

  /** Personalised headline name, e.g. "Pick a plan to keep going, Grace.". */
  readonly firstName = computed<string>(() => this.shoutUser()?.firstName ?? 'there');

  readonly checking = signal<boolean>(false);
  readonly actionError = signal<string | null>(null);

  /** Primary CTA — open the existing Choose Plan modal (→ Stripe Checkout). */
  choosePlan(): void {
    this.actionError.set(null);
    this.choosePlanService.subscribeModalOpen.set(true);
  }

  /** Secondary CTA — re-check the subscription; restore access if one is now active. */
  async alreadySubscribed(): Promise<void> {
    if (this.checking()) {
      return;
    }
    this.actionError.set(null);
    this.checking.set(true);
    try {
      await this.currentUserService.refreshFocussedSubscription();
      if (this.currentUserService.subscriptionActive()) {
        this.router.navigate(['/dashboard']);
        return;
      }
      this.actionError.set('We couldn’t find an active subscription yet — choose a plan to get started.');
    } catch (error) {
      console.error('Failed to refresh subscription', error);
      this.actionError.set('We couldn’t find an active subscription yet — choose a plan to get started.');
    } finally {
      this.checking.set(false);
    }
  }
}
